import { CommonModule, Location } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { IonicModule, ModalController, NavController } from '@ionic/angular';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { Chart, LinearScale } from 'chart.js';
import { Flow, SankeyController } from 'chartjs-chart-sankey';
import Markdown from 'markdown-it';
import moment from 'moment-timezone';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AiUsageComponent } from 'src/app/analytics/ai-usage/ai-usage.component';
import { DatetimeModalComponent } from 'src/app/components/datetime-modal/datetime-modal.component';
import { FilterButtonComponent } from 'src/app/components/filter-button/filter-button.component';
import { CONVERSATION_STATUS, DATE_OPTIONS, DATE_RANGE_TYPES } from 'src/app/constants';
import { FilterOption, SelectedFilterMap } from 'src/app/utils/filter-utils';
import { downloadCSV, getHtmlFromSlackMd, getWhereClausesFromFilters } from 'src/app/utils/utils';
import { FetchDataRequest, FetchDataResponse, createDataSource } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { AiResponse, DEFLECTION_STATUS_FILTERS, WhereClause } from 'src/models';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

interface DeflectedStatusCounts {
  stale?: number;
  closedBySubmitter?: number;
  closedByInternalUser?: number;
  closedByExternalUser?: number;
}

const SANKEY_LABELS = {
  TOTAL_CONVERSATIONS: 'Total conversations',
  NON_DEFLECTED_CONVERSATIONS: 'Non-deflected conversations',
  DEFLECTED_CONVERSATIONS: 'Deflected conversations',
  STALE_INTAKE: 'Stale intake',
  CLOSED_BY_SUBMITTER: 'Closed by submitter',
  CLOSED_BY_INTERNAL_USER: 'Closed by internal user',
  CLOSED_BY_EXTERNAL_USER: 'Closed by external user',
};

// Original color mapping using base labels
const colors = {
  [SANKEY_LABELS.TOTAL_CONVERSATIONS]: 'rgb(255, 177, 66)',
  [SANKEY_LABELS.NON_DEFLECTED_CONVERSATIONS]: 'rgb(255, 82, 82)',
  [SANKEY_LABELS.DEFLECTED_CONVERSATIONS]: '#2fdf75',
  [SANKEY_LABELS.STALE_INTAKE]: '#333333',
  [SANKEY_LABELS.CLOSED_BY_SUBMITTER]: '#2fdf75',
  [SANKEY_LABELS.CLOSED_BY_INTERNAL_USER]: '#a97bd9',
  [SANKEY_LABELS.CLOSED_BY_EXTERNAL_USER]: '#0080ff',
};

interface ConversationCountCall {
  baseStatuses: {
    open: number;
    inProgress: number;
    closed: number;
    draft: number;
    onHold: number;
  };
  deflectedStatuses: DeflectedStatusCounts;
}

Chart.register(SankeyController, Flow, LinearScale);

@Component({
  standalone: true,
  selector: 'app-ai-analytics',
  templateUrl: './ai-analytics.component.html',
  styleUrls: ['./ai-analytics.component.scss'],
  imports: [
    CommonModule,
    SharedDirectivesModule,
    FormsModule,
    IonicModule,
    RouterModule,
    NgxTippyModule,
    NgxDatatableModule,
    FilterButtonComponent,
    AiUsageComponent,
  ],
})
export class AiAnalyticsComponent implements OnInit, OnDestroy {
  @ViewChild('myChart') myChartCanvas: ElementRef<HTMLCanvasElement>;
  chart: Chart;
  private themeObserver: MutationObserver;
  private routerSub: Subscription;

  // Tab state
  tab: 'deflection' | 'usage' = 'deflection';

  filterType: 'all' | 'thumbs_up' | 'thumbs_down' | 'no_response' | 'has_response' = 'all';
  feedbackItems = [];
  isInitialized = false;
  isLoading: boolean;
  allAiResponses: number;
  deflectedAiResponses: number;
  deflectedAiResponseCounts: DeflectedStatusCounts;
  hasChartData: boolean = false;
  dateRange = DATE_OPTIONS[2];
  draft = 0;
  readonly draftStatus = CONVERSATION_STATUS.INTAKE;
  inProgress = 0;
  open = 0;
  closed = 0;
  isDateRangeInvalid: boolean;
  startDate: Date | null;
  endDate: Date | null;
  timezone: string;
  limit = 50;
  cursor: any;
  sortField = 'createdAt';
  sortDesc = true;

  filterOptions: FilterOption[] = [
    {
      label: 'Ticket Type',
      id: 'ticketTypeId',
      type: 'select',
    },
    {
      label: 'Project',
      id: 'projectId',
      type: 'select',
    },
    {
      label: 'Deflection Status',
      id: 'deflectionStatus',
      type: 'select',
      values: [
        { id: DEFLECTION_STATUS_FILTERS.NON_DEFLECTED, label: 'Non-deflected conversations' },
        { id: DEFLECTION_STATUS_FILTERS.DEFLECTED, label: 'Deflected conversations' },
        { id: DEFLECTION_STATUS_FILTERS.STALE, label: 'Stale intake' },
        { id: DEFLECTION_STATUS_FILTERS.CLOSED_BY_SUBMITTER, label: 'Closed by submitter' },
        { id: DEFLECTION_STATUS_FILTERS.CLOSED_BY_INTERNAL_USER, label: 'Closed by internal user' },
        { id: DEFLECTION_STATUS_FILTERS.CLOSED_BY_EXTERNAL_USER, label: 'Closed by external user' },
      ],
    },
    {
      label: 'User Feedback',
      id: 'feedbackType',
      type: 'select',
      values: [
        { id: 'NO_FEEDBACK', label: 'No feedback' },
        { id: 'thumbs_up', label: 'Thumbs up' },
        { id: 'thumbs_down', label: 'Thumbs down' },
      ],
    },
  ];
  filters: SelectedFilterMap = new Map();

  constructor(
    private apiService: ApiService,
    private analyticsService: AnalyticsService,
    private modalCtrl: ModalController,
    private searchablePopoverService: SearchablePopoverService,
    public authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private navCtrl: NavController,
  ) {}

  async ngOnInit() {
    this.isInitialized = false;

    // Subscribe to query params for tab changes
    this.route.queryParams.subscribe(async (params) => {
      const isUsageEnabled = this.authService.tenant.featureFlags?.AI_USAGE_DASHBOARD === true;
      const newTab = params.view === 'usage' && isUsageEnabled ? 'usage' : 'deflection';
      const wasOnDifferentTab = this.tab !== newTab;

      this.tab = newTab;

      // If switching to deflection tab
      if (this.tab === 'deflection' && wasOnDifferentTab) {
        if (!this.isInitialized) {
          // First time - load all data
          await this.initializeDeflectionData();
        } else {
          // Coming back - reload both chart and table data since the canvas was destroyed by *ngIf
          // Reload table data
          await this.loadResponses();
          // Use setTimeout to wait for the DOM to render the canvas
          setTimeout(() => {
            this.loadChart();
          }, 0);
        }
      }
    });

    // Only load deflection data on init if starting on that tab
    if (this.tab === 'deflection') {
      await this.initializeDeflectionData();
    }

    // MutationObserver for theme changes
    this.themeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          console.log('Theme changed, reloading chart...'); // Optional logging
          // Destroy existing chart and reload to apply new theme options
          if (this.chart) {
            this.chart.destroy();
          }
          this.loadChart();
        }
      });
    });

    this.themeObserver.observe(document.documentElement, {
      attributes: true, // Observe attribute changes
    });
  }

  private async initializeDeflectionData() {
    await this.loadResponses();

    this.timezone = moment.tz.guess();
    this.endDate = moment().toDate();
    this.startDate = moment(this.endDate).startOf('day').subtract(7, 'days').toDate();

    await this.getConversationResponses();
  }

  ngOnDestroy(): void {
    // Add OnDestroy lifecycle hook
    if (this.themeObserver) {
      this.themeObserver.disconnect(); // Disconnect observer on component destroy
    }
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  loadChart() {
    if (!this.myChartCanvas) {
      console.error('Canvas element not found for chart.');
      return;
    }
    const ctx = this.myChartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      console.error('Could not get 2D context from canvas.');
      return;
    }

    // Function to get color based on the original label
    const getColor = (key) => {
      if (!key || typeof key !== 'string') {
        return 'gray'; // Fallback for invalid keys
      }
      // Extract the original label part if the key includes a count
      const baseLabel = key.split(' (')[0];
      return colors[baseLabel] || 'gray'; // Fallback color
    };

    // Calculate totals and create combined labels
    const totalFlow = this.allAiResponses || 0;
    const nonDeflectedFlow = totalFlow - (this.deflectedAiResponses || 0);
    const totalLabel = `${SANKEY_LABELS.TOTAL_CONVERSATIONS} (${totalFlow})`;
    const nonDeflectedLabel = `${SANKEY_LABELS.NON_DEFLECTED_CONVERSATIONS} (${nonDeflectedFlow})`;
    const deflectedLabel = `${SANKEY_LABELS.DEFLECTED_CONVERSATIONS} (${this.deflectedAiResponses || 0})`;
    const staleLabel = `${SANKEY_LABELS.STALE_INTAKE} (${this.deflectedAiResponseCounts?.stale || 0})`;
    const closedSubmitterLabel = `${SANKEY_LABELS.CLOSED_BY_SUBMITTER} (${this.deflectedAiResponseCounts?.closedBySubmitter || 0})`;
    const closedInternalLabel = `${SANKEY_LABELS.CLOSED_BY_INTERNAL_USER} (${this.deflectedAiResponseCounts?.closedByInternalUser || 0})`;
    const closedExternalLabel = `${SANKEY_LABELS.CLOSED_BY_EXTERNAL_USER} (${this.deflectedAiResponseCounts?.closedByExternalUser || 0})`;

    // Prepare chart data using combined labels
    const chartData = [
      // Only add these items if flow values are valid numbers
      ...(nonDeflectedFlow > 0 ? [{ from: totalLabel, to: nonDeflectedLabel, flow: nonDeflectedFlow }] : []),
      ...(this.deflectedAiResponses && this.deflectedAiResponses > 0
        ? [{ from: totalLabel, to: deflectedLabel, flow: this.deflectedAiResponses }]
        : []),
      // Use combined labels for deflected breakdowns
      ...(this.deflectedAiResponseCounts?.stale
        ? [{ from: deflectedLabel, to: staleLabel, flow: this.deflectedAiResponseCounts.stale }]
        : []),
      ...(this.deflectedAiResponseCounts?.closedBySubmitter
        ? [{ from: deflectedLabel, to: closedSubmitterLabel, flow: this.deflectedAiResponseCounts.closedBySubmitter }]
        : []),
      ...(this.deflectedAiResponseCounts?.closedByInternalUser
        ? [{ from: deflectedLabel, to: closedInternalLabel, flow: this.deflectedAiResponseCounts.closedByInternalUser }]
        : []),
      ...(this.deflectedAiResponseCounts?.closedByExternalUser
        ? [{ from: deflectedLabel, to: closedExternalLabel, flow: this.deflectedAiResponseCounts.closedByExternalUser }]
        : []),
    ].filter((item) => item.flow > 0); // Ensure we don't add flows with 0 value if counts are missing

    // Track if we have data to display
    this.hasChartData = chartData.length > 0;

    // If no chart data after processing, don't create the chart
    if (!this.hasChartData) {
      return;
    }

    this.chart = new Chart(ctx, {
      type: 'sankey',
      data: {
        datasets: [
          {
            label: 'deflections_dataset',
            data: chartData, // Use the updated chartData
            colorFrom: (c) => {
              const dataItem = c.dataset.data[c.dataIndex];
              return dataItem && dataItem.from ? getColor(dataItem.from) : '#cccccc';
            },
            colorTo: (c) => {
              const dataItem = c.dataset.data[c.dataIndex];
              return dataItem && dataItem.to ? getColor(dataItem.to) : '#cccccc';
            },
            color: this.isDarkMode() ? '#ffffff' : '#000000',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,

        font: {
          size: 16,
        },
        layout: {
          padding: {
            bottom: 20,
          },
        },
        onClick: (event, elements, chart) => {
          this.handleChartClick(event, elements, chart);
        },
        onHover: (event, elements, chart) => {
          // Change cursor to pointer when hovering over clickable elements
          const canvas = chart.canvas;
          if (canvas) {
            canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
          }
        },
      } as any,
    });
  }

  isDarkMode(): boolean {
    return localStorage.getItem('theme') === 'dark';
  }

  /**
   * Handle clicks on Sankey chart nodes to apply filters to the table
   */
  handleChartClick(event: any, elements: any[], chart: Chart) {
    if (!elements || elements.length === 0) {
      return;
    }

    // Get the clicked element
    const element = elements[0];

    // For Sankey charts, we need to determine if a node was clicked
    // The element may have information about which node (from/to) was clicked
    if (!element || typeof element.index !== 'number') {
      return;
    }

    // Get the dataset and data point
    const dataset = chart.data.datasets[element.datasetIndex];
    const dataPoint = dataset.data[element.index] as any;

    if (!dataPoint) {
      return;
    }

    // Try to get the clicked node label from the flow data
    // In Sankey charts, clicks are on flows (edges) connecting nodes
    // We prioritize the 'to' node (destination) as that's typically what users want to filter by
    let clickedLabel: string | null = null;

    // Get the 'to' node label (right side) - this is more likely what the user clicked
    if (dataPoint.to) {
      clickedLabel = dataPoint.to;
    } else if (dataPoint.from) {
      clickedLabel = dataPoint.from;
    }

    if (!clickedLabel) {
      return;
    }

    // Extract the base label (remove the count in parentheses)
    const baseLabel = clickedLabel.split(' (')[0];

    // Map the label to the filter value
    let filterValue: string | null = null;

    switch (baseLabel) {
      case SANKEY_LABELS.NON_DEFLECTED_CONVERSATIONS:
        filterValue = DEFLECTION_STATUS_FILTERS.NON_DEFLECTED;
        break;
      case SANKEY_LABELS.DEFLECTED_CONVERSATIONS:
        filterValue = DEFLECTION_STATUS_FILTERS.DEFLECTED;
        break;
      case SANKEY_LABELS.STALE_INTAKE:
        filterValue = DEFLECTION_STATUS_FILTERS.STALE;
        break;
      case SANKEY_LABELS.CLOSED_BY_SUBMITTER:
        filterValue = DEFLECTION_STATUS_FILTERS.CLOSED_BY_SUBMITTER;
        break;
      case SANKEY_LABELS.CLOSED_BY_INTERNAL_USER:
        filterValue = DEFLECTION_STATUS_FILTERS.CLOSED_BY_INTERNAL_USER;
        break;
      case SANKEY_LABELS.CLOSED_BY_EXTERNAL_USER:
        filterValue = DEFLECTION_STATUS_FILTERS.CLOSED_BY_EXTERNAL_USER;
        break;
      case SANKEY_LABELS.TOTAL_CONVERSATIONS:
        // Clicking on "Total conversations" clears the deflection status filter
        const deflectionFilterToClear = this.filterOptions.find((opt) => opt.id === 'deflectionStatus');
        if (deflectionFilterToClear) {
          this.filters.delete(deflectionFilterToClear);
        }
        this.loadResponses();
        return;
      default:
        return;
    }

    if (filterValue) {
      // Find the deflection status filter option
      const deflectionStatusOption = this.filterOptions.find((opt) => opt.id === 'deflectionStatus');

      if (deflectionStatusOption) {
        // Update the filters map with the deflection status filter
        this.filters.set(deflectionStatusOption, [{ id: filterValue, label: baseLabel }]);

        // Reload the table with the new filter
        this.loadResponses();
      }
    }
  }

  renderMarkdown(markdown: string) {
    const md = new Markdown('commonmark', {
      xhtmlOut: true,
      breaks: true,
      html: true,
    });

    const html = md.render(markdown);

    return html;
  }

  getDateRange() {
    const params: any = {
      timezone: this.timezone,
    };

    if (this.dateRange.type === DATE_RANGE_TYPES.RELATIVE) {
      // Unlike with standard reporting, we want this to include the current day
      params.dateRange = {
        ...this.dateRange,
        endIndex: 0,
      };
    } else {
      params.dateRange = {
        type: DATE_RANGE_TYPES.ABSOLUTE,
        startDate: moment(this.startDate).format('YYYY-MM-DD'),
        endDate: moment(this.endDate).format('YYYY-MM-DD'),
      };
    }

    return params;
  }

  async changeDate(isEnd: boolean) {
    const datePickerModal = await this.modalCtrl.create({
      component: DatetimeModalComponent,
      cssClass: 'datetime-modal',
      componentProps: {
        min: isEnd ? this.startDate : null,
        selected: isEnd ? this.endDate : this.startDate,
        title: isEnd ? 'Select end date' : 'Select start date',
      },
    });

    await datePickerModal.present();

    datePickerModal.onDidDismiss().then((data) => {
      const result = data?.data?.value;

      if (result && result !== 'Invalid date') {
        const newDate = moment(result).toDate();

        if (isEnd) {
          this.endDate = newDate;
        } else {
          this.startDate = newDate;
        }

        this.isDateRangeInvalid = this.startDate.getTime() > this.endDate.getTime();

        if (!this.isDateRangeInvalid) {
          this.getConversationResponses();
        }
      }
    });
  }

  async showDateRangeOptions() {
    this.searchablePopoverService.dateValues({
      event,
      callback: (id: string, _name: string, object: any) => {
        this.dateRange = object;

        this.isDateRangeInvalid =
          this.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE && this.startDate.getTime() > this.endDate.getTime();

        if (!this.isDateRangeInvalid) {
          this.getConversationResponses();
        }
      },
    });
  }

  async getConversationResponses() {
    const dateData = this.getDateRange();

    // Add project filtering if a project is selected
    const projectParam = this.authService.selectedProject ? `&projectId=${this.authService.selectedProject.id}` : '';

    const result = await Promise.all([
      this.apiService
        .getPromise(
          `/reporting/conversation-count-by-status/?dateRange=${JSON.stringify(dateData.dateRange)}&timezone=${dateData.timezone}${projectParam}`,
        )
        .then((res: ConversationCountCall) => {
          res.baseStatuses.closed = res.baseStatuses.closed || 0;
          res.baseStatuses.inProgress = res.baseStatuses.inProgress || 0;
          res.baseStatuses.draft = res.baseStatuses.draft || 0;
          res.baseStatuses.open = res.baseStatuses.open || 0;
          res.baseStatuses.onHold = res.baseStatuses.onHold || 0;

          this.allAiResponses =
            (res.baseStatuses.draft || 0) +
            (res.baseStatuses.closed || 0) +
            (res.baseStatuses.inProgress || 0) +
            (res.baseStatuses.open || 0) +
            (res.baseStatuses.onHold || 0);
          this.deflectedAiResponseCounts = res.deflectedStatuses;
          this.deflectedAiResponses = Object.values(res.deflectedStatuses ?? {}).reduce((acc, curr) => acc + curr, 0);
          this.isInitialized = true;

          // Destroy existing chart and reload with new data after data is updated
          if (this.chart) {
            this.chart.destroy();
          }
          this.loadChart();
        })
        .catch((err) => {
          console.log('error: ', err);
        }),
      this.loadResponses(),
    ]);

    return result;
  }

  async exportAsCsv() {
    // emit analytics events
    this.analyticsService.trackEvent('AI Analytics', 'Export CSV');
    // create date object
    const date = new Date();
    // retrieve and map data to headers
    // initial fetch, then loop over pages using cursor
    const pages = [];
    let cursors;

    do {
      const nextPage = await this.fetchResponses({
        limit: this.limit,
        order: [this.sortField],
        descending: this.sortDesc,
        cursor: cursors?.next,
      });

      pages.push(nextPage);
      cursors = nextPage.cursors;
    } while (cursors.hasNext);
    // flatten and format results
    const results = pages.flatMap((page) => {
      return page.data.map((row) => {
        return {
          query: row.query,
          aiResponse: row.response,
          userFeedback: JSON.stringify(row.feedbacks),
          customer: row.conversation?.customer?.name || null,
          ticketType: row.conversation?.ticketType?.name || null,
          created: row.createdAt,
        };
      });
    });
    // declare headers
    const headers = ['query', 'aiResponse', 'userFeedback', 'customer', 'ticketType', 'created'];
    // export combination as CSV
    downloadCSV(results, headers, `ai-analytics-${date.getTime()}`);
  }

  fetchResponses = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<AiResponse>>> => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
    };

    const where: WhereClause[] = [];

    const { dateRange, timezone } = this.getDateRange();

    options.dateRange = dateRange;
    options.timezone = timezone;

    // Filter to only show original customer-facing deflection sources
    // (agent and autoresponder were the sources that existed before recent tracking additions)
    where.push({
      field: 'trigger',
      operator: 'in',
      value: ['agent', 'autoresponder'],
    });

    if (this.filterType === 'no_response') {
      where.push({
        field: 'response',
        operator: '==',
        value: '',
      });
    } else if (this.filterType === 'has_response') {
      where.push({
        field: 'response',
        operator: '!=',
        value: '',
      });
    }

    // Add project filtering if a project is selected
    if (this.authService.selectedProject) {
      where.push({
        field: 'projectId',
        operator: '==',
        value: this.authService.selectedProject.id,
      });
    }

    const filterWheres = getWhereClausesFromFilters(this.filters);

    if (filterWheres.length) {
      where.push(...filterWheres);
    }

    if (where.length) {
      options.where = where;
    }

    // TODO create a service
    let result = (await this.apiService.postPromise('/ai-responses/list', options)) as any;

    result.data = result.data.map((aiRes) => {
      return {
        ...aiRes,
        formattedQuery: aiRes.query ? getHtmlFromSlackMd(aiRes.query) : '',
        formattedResponse: aiRes.response ? this.renderMarkdown(aiRes.response) : '',
      };
    });

    return result;
  };

  responsesDateSource = createDataSource(this.fetchResponses);

  async loadResponses() {
    this.isLoading = true;

    await this.responsesDateSource.firstPage();

    this.isLoading = false;
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;
    this.loadResponses();
  }

  /**
   * Switch between tabs and update URL with query params
   */
  selectTab(tab: 'deflection' | 'usage') {
    // Don't set this.tab directly - let the query params subscription handle it
    // This way wasOnDifferentTab will be calculated correctly
    this.navCtrl.navigateForward(this.router.url.split('?')[0], {
      queryParams: tab === 'usage' ? { view: tab } : {},
    });
  }
}
